import { AloftTime, BackendAccount } from '@aloft/toolkit-shared';
import {
  Button,
  Flex,
  Input,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Typography,
} from '@aloft/toolkit-sui';

import { useState } from 'react';

import { LoadingScreen } from '../../../components/LoadingScreen/LoadingScreen.tsx';
import { useListAccountsAPI } from '../../../hooks/api/schema-hooks/useListAccountsAPI.ts';
import { selectAccount } from '../../../redux/features/selectors/accountSelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';

const ACCOUNTS_PER_PAGE = 20;

export const UserManagementContent = () => {
  const [page, setPage] = useState(0);
  const [submitEmail, setSubmitEmail] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [submitName, setSubmitName] = useState<string>('');
  const [name, setName] = useState<string>('');
  const listAccounts = useListAccountsAPI({
    queryParams: {
      limit: String(ACCOUNTS_PER_PAGE),
      offset: String(page * ACCOUNTS_PER_PAGE),
      email: submitEmail,
      name: submitName,
    },
  });
  const currentAccount = useAppSelector(selectAccount);

  const impersonateAccount = (account: BackendAccount) => {
    localStorage.setItem('impersonation-user-id', account.id);
    window.location.replace('/');
  };

  if (listAccounts.isLoading) return <LoadingScreen what="User Accounts" />;
  if (listAccounts.isError) {
    throw Error(`Failed to load user accounts: ${listAccounts.error as string}`);
  }

  return (
    <Table>
      <Thead>
        <Tr>
          <Th colSpan={2}>
            <Input
              type="text"
              size="sm"
              label="Filter by email"
              value={email}
              onClear={() => setEmail('')}
              onChange={(value) => setEmail(value)}
              onBlur={() => setSubmitEmail(email)}
            />
          </Th>
          <Th colSpan={3}>
            <Input
              type="text"
              size="sm"
              label="Filter by name"
              value={name}
              onClear={() => setName('')}
              onChange={(value) => setName(value)}
              onBlur={() => setSubmitName(name)}
            />
          </Th>
        </Tr>
        <Tr>
          <Th textAlign="left">
            <Typography variant="h5">ID</Typography>
          </Th>
          <Th textAlign="left">
            <Typography variant="h5">Name</Typography>
          </Th>
          <Th textAlign="left">
            <Typography variant="h5">Email</Typography>
          </Th>
          <Th textAlign="left">
            <Typography variant="h5">Phone</Typography>
          </Th>
          <Th textAlign="left">
            <Typography variant="h5">Admin</Typography>
          </Th>
          <Th textAlign="left">
            <Typography variant="h5">User since</Typography>
          </Th>
          <Th textAlign="left">
            <Typography variant="h5">Actions</Typography>
          </Th>
        </Tr>
      </Thead>
      <Tbody>
        {listAccounts.data?.results.map((account) => (
          <Tr key={account.id}>
            <Td>{account.id}</Td>
            <Td>
              {account.body.isOnboardingComplete === false
                ? '(onboarding)'
                : `${account.body.firstName} ${account.body.lastName}`}
            </Td>
            <Td>{account.email}</Td>
            <Td>{account.body.phone || 'N/A'}</Td>
            <Td>{account.isAdmin ? 'Yes' : 'No'}</Td>
            <Td>{AloftTime.schema.parse(account.createdAt).toAloftDate().toString()}</Td>
            <Td>
              {currentAccount.id !== account.id && account.body.isOnboardingComplete !== false && (
                <Button onClick={() => impersonateAccount(account)} text="Impersonate" size="sm" />
              )}
            </Td>
          </Tr>
        ))}
      </Tbody>
      <tfoot>
        <Tr>
          <Td colSpan={6}>
            <Typography variant="body2">
              Showing {listAccounts.data?.resultsCount.toString()} of&nbsp;
              {listAccounts.data.totalResultsCount.toString()} users
            </Typography>
          </Td>
          <Td colSpan={6}>
            <Flex gap={2} justifyContent="flex-end" alignItems="center">
              <Button
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                text="Previous"
                variant="secondary"
              />
              <Button
                disabled={(page + 1) * ACCOUNTS_PER_PAGE >= listAccounts.data.totalResultsCount}
                text="Next"
                onClick={() => setPage((p) => p + 1)}
                variant="secondary"
              />
            </Flex>
          </Td>
        </Tr>
      </tfoot>
    </Table>
  );
};
